import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { LABEL_KIND_LABEL_HU, LABEL_PRINT_STATUS_LABEL_HU } from '@persped/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLabelBatches } from '../../api/queries';
import { PrintStatusChip } from '../../components/PrintStatusChip';
import { ProblemAlert } from '../../components/ProblemAlert';
import { formatTimestamp } from '../../format';
import { AllocateLabelsDialog } from './AllocateLabelsDialog';
import { NewFolderBatchDialog } from './NewFolderBatchDialog';
import { NewLocationBatchDialog } from './NewLocationBatchDialog';

const PRINT_STATUSES = Object.keys(LABEL_PRINT_STATUS_LABEL_HU) as Array<
  keyof typeof LABEL_PRINT_STATUS_LABEL_HU
>;

/**
 * Címkék — a nyomtatási kötegek listája és története (B9, D fázis 9.1–9.2).
 *
 * A terv szerint ez egyben a TEENDŐLISTA: a megerősítésre váró kötegek
 * (`SENT`/`UNCONFIRMED`) azok, amiket valakinek fizikailag ellenőriznie
 * kell. Ezért alapértelmezetten a selejtezetteket elrejtjük — azok
 * lezárt ügyek.
 */
export function LabelBatchListPage() {
  const navigate = useNavigate();
  const [printStatus, setPrintStatus] = useState('');
  const [labelKind, setLabelKind] = useState('');
  const [includeVoided, setIncludeVoided] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [folderDialog, setFolderDialog] = useState(false);
  const [locationDialog, setLocationDialog] = useState(false);
  const [allocateDialog, setAllocateDialog] = useState(false);

  const batches = useLabelBatches({
    printStatus: printStatus || undefined,
    labelKind: labelKind || undefined,
    includeVoided,
    page: page + 1,
    pageSize,
  });

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
          Címkék
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setFolderDialog(true)}>
          Mappacímkék nyomtatása
        </Button>
        <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setLocationDialog(true)}>
          Lokációcímkék
        </Button>
        <Button variant="outlined" color="secondary" onClick={() => setAllocateDialog(true)}>
          Sorszám foglalása
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            select
            label="Nyomtatási státusz"
            value={printStatus}
            onChange={(e) => {
              setPrintStatus(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Mind</MenuItem>
            {PRINT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {LABEL_PRINT_STATUS_LABEL_HU[s]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Címke típusa"
            value={labelKind}
            onChange={(e) => {
              setLabelKind(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Mind</MenuItem>
            {/* A négy objektumtípus mind külön címketípus (D fázis, 3.2). */}
            {Object.entries(LABEL_KIND_LABEL_HU).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={
              <Checkbox
                checked={includeVoided}
                onChange={(e) => {
                  setIncludeVoided(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Selejtezettek is"
          />
        </Stack>
      </Paper>

      {batches.isError && <ProblemAlert error={batches.error} />}

      {batches.isPending ? (
        <Stack sx={{ py: 6, alignItems: 'center' }}>
          <CircularProgress />
        </Stack>
      ) : batches.data ? (
        <Paper variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Létrehozva</TableCell>
                  <TableCell>Típus</TableCell>
                  <TableCell align="right">Darab</TableCell>
                  <TableCell>Tartomány</TableCell>
                  <TableCell>Státusz</TableCell>
                  <TableCell>Megerősítve</TableCell>
                  <TableCell>Megjegyzés</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.data.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        Nincs a szűrésnek megfelelő köteg.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {batches.data.items.map((batch) => (
                  <TableRow
                    key={batch.id}
                    hover
                    sx={{ cursor: 'pointer', opacity: batch.voidedAt ? 0.55 : 1 }}
                    onClick={() => void navigate(`/cimkek/${batch.id}`)}
                  >
                    <TableCell>{formatTimestamp(batch.createdAt)}</TableCell>
                    <TableCell>{LABEL_KIND_LABEL_HU[batch.labelKind]}</TableCell>
                    <TableCell align="right">{batch.requestedCount}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {batch.firstBarcode && batch.lastBarcode
                        ? `${batch.firstBarcode} – ${batch.lastBarcode}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <PrintStatusChip status={batch.printStatus} />
                        {batch.voidedAt && (
                          <Typography variant="caption" color="text.secondary">
                            selejtezve
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatTimestamp(batch.confirmedAt)}</TableCell>
                    <TableCell>
                      <Box sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {batch.note ?? '—'}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={batches.data.total}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Sorok oldalanként:"
          />
        </Paper>
      ) : null}

      <NewFolderBatchDialog open={folderDialog} onClose={() => setFolderDialog(false)} />
      <NewLocationBatchDialog open={locationDialog} onClose={() => setLocationDialog(false)} />
      <AllocateLabelsDialog open={allocateDialog} onClose={() => setAllocateDialog(false)} />
    </Stack>
  );
}
